import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import guestRequestApi from '../services/guestRequestApi';
import userApi from '../services/userApi';
import { formatRelativeTime } from '../utils/dateFormatter';
import { todayLocalIso, toInputDateValue as dateToInputValue } from '../utils/dateValueHelper';
import { GR_TYPE_LABELS, GR_PRIORITY_LABELS } from '../config/guestRequestLabels';
import '../styles/LinkTaskModal.css';

// v1.1.16-D — prefilling <input type="date"> from GR.requestedForDate used to be
// a local copy of toLocalIso; it now delegates to utils/dateValueHelper.

// LinkTaskModal — V1.1 GR-to-Task escalation surface.
// Spec: tasks/grh-v1.1-aggregation.md
//
// Smaller and more focused than the global CreateTaskModal because most
// Task fields are derived deterministically from the GR (type, priority,
// title, notes, property, booking, taskSource, guestRequestId). Ops only
// chooses execution metadata: when, optional time, optional assignee.
//
// Atomic backend: POST /api/v1/guest-requests/:id/link-task
// Returns the fully-decorated GR for immediate row replacement on the parent.

const LinkTaskModal = ({ show, onHide, request, onLinked }) => {
  const [staffUsers, setStaffUsers] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // In-modal success state — shows the green confirmation with the new
  // Task's ticketId for ~1.5s before the modal closes. Mirrors the pattern
  // in CreateTaskModal.jsx (the canonical task-creation flow).
  const [successTicketId, setSuccessTicketId] = useState(null);
  const [formData, setFormData] = useState({
    // v1.1.16-D — prefill from GR's intake-layer date when set. Ops can override.
    scheduledDate: dateToInputValue(request?.requestedForDate) || todayLocalIso(),
    scheduledTime: '',
    taskAssignedTo: ''
  });

  // Load staff list when modal opens. Reset state on each open so a previous
  // submission's error doesn't leak into the next interaction.
  useEffect(() => {
    if (!show) return;
    setError(null);
    setSuccessTicketId(null);
    setFormData({
      // v1.1.16-D — re-prefill on each open in case the parent passed a
      // different GR (e.g. ops navigated between rows on the hub page).
      scheduledDate: dateToInputValue(request?.requestedForDate) || todayLocalIso(),
      scheduledTime: '',
      taskAssignedTo: ''
    });

    let cancelled = false;
    const loadStaff = async () => {
      setLoadingStaff(true);
      try {
        const data = await userApi.getStaffUsers();
        if (!cancelled) setStaffUsers(data?.users || []);
      } catch (err) {
        // Non-fatal — assignee dropdown stays empty, ops can submit unassigned.
        console.error('Failed to load staff users:', err);
      } finally {
        if (!cancelled) setLoadingStaff(false);
      }
    };
    loadStaff();
    return () => { cancelled = true; };
  }, [show]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!request?._id) return;

    if (!formData.scheduledDate) {
      setError('تاريخ التنفيذ مطلوب');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        scheduledDate: formData.scheduledDate,
      };
      if (formData.scheduledTime) payload.scheduledTime = formData.scheduledTime;
      if (formData.taskAssignedTo) payload.taskAssignedTo = formData.taskAssignedTo;

      const resp = await guestRequestApi.linkTask(request._id, payload);
      if (resp?.success && resp.request) {
        // Show in-modal success briefly so ops sees the new ticketId before
        // the modal closes. Pattern matches CreateTaskModal.jsx:131-142.
        setSubmitting(false);
        setSuccessTicketId(resp.request.linkedTaskTicketId || null);
        setTimeout(() => {
          if (onLinked) onLinked(resp.request);
          onHide();
        }, 2000);
      } else {
        setError('تعذّر ربط المهمة. حاول مجددًا.');
        setSubmitting(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.error || 'تعذّر ربط المهمة. حاول مجددًا.';
      setError(msg);
      setSubmitting(false);
    }
  }, [request, formData, onLinked, onHide]);

  if (!request) return null;

  return (
    <Modal show={show} onHide={onHide} dir="rtl" centered className="link-task-modal">
      <Modal.Header className="link-task-modal__header">
        <Modal.Title>إنشاء مهمة من طلب الضيف</Modal.Title>
        <button
          type="button"
          className="btn-close"
          aria-label="إغلاق"
          onClick={onHide}
        />
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="link-task-context">
            <div className="link-task-context__head">
              <span className={`grs-type grs-type--${request.type}`}>
                {GR_TYPE_LABELS[request.type] || request.type}
              </span>
              <span className={`grs-priority grs-priority--${request.priority}`}>
                {GR_PRIORITY_LABELS[request.priority] || request.priority}
              </span>
            </div>
            {request.description && (
              <p className="link-task-context__desc">{request.description}</p>
            )}
            <div className="link-task-context__meta">
              <span><i className="bi bi-person"></i>{' '}{request.displayGuestName || '—'}</span>
              {request.displayBookingCode && (
                <span className="link-task-context__booking">
                  <i className="bi bi-bookmark"></i>{' '}{request.displayBookingCode}
                </span>
              )}
              <span><i className="bi bi-house"></i>{' '}{request.displayPropertyName || '—'}</span>
              <span><i className="bi bi-clock"></i>{' '}{formatRelativeTime(request.createdAt)}</span>
            </div>
            <p className="link-task-context__hint">
              سيتم إنشاء مهمة ميدانية مرتبطة بهذا الطلب. النوع والأولوية محددان من الطلب.
            </p>
          </div>

          {error && <Alert variant="danger" className="link-task-error">{error}</Alert>}

          {successTicketId && (
            <Alert variant="success" className="link-task-error">
              تم إنشاء المهمة #<strong>{successTicketId}</strong> بنجاح
            </Alert>
          )}

          <div className="link-task-fields">
            <Form.Group className="link-task-field">
              <Form.Label>تاريخ التنفيذ <span className="required-mark">*</span></Form.Label>
              <Form.Control
                type="date"
                name="scheduledDate"
                value={formData.scheduledDate}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="link-task-field">
              <Form.Label>الوقت (اختياري)</Form.Label>
              <Form.Control
                type="time"
                name="scheduledTime"
                value={formData.scheduledTime}
                onChange={handleChange}
              />
              <Form.Text className="text-muted">صيغة 24 ساعة</Form.Text>
            </Form.Group>

            <Form.Group className="link-task-field link-task-field--full">
              <Form.Label>المسؤول عن التنفيذ (اختياري)</Form.Label>
              <Form.Select
                name="taskAssignedTo"
                value={formData.taskAssignedTo}
                onChange={handleChange}
                disabled={loadingStaff}
              >
                <option value="">{loadingStaff ? 'جارٍ تحميل الموظفين...' : 'بدون تعيين'}</option>
                {staffUsers.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.name || u.email}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                إن لم تختر موظفًا، ستُنشأ المهمة بحالة "قيد الانتظار" ويعيِّنها المشرف لاحقًا.
              </Form.Text>
            </Form.Group>
          </div>
        </Modal.Body>

        <Modal.Footer className="link-task-modal__footer">
          <Button variant="secondary" onClick={onHide} disabled={submitting || !!successTicketId}>
            إلغاء
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={submitting || !!successTicketId}
            className="link-task-submit-btn"
          >
            {submitting ? (
              <><Spinner size="sm" animation="border" className="me-2" /> جارٍ الإنشاء...</>
            ) : (
              <><i className="bi bi-plus-circle"></i> إنشاء المهمة</>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default LinkTaskModal;
